import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMySchedule, confirmSessions, confirmAllSessions, declineAllSessions, getComplianceCheck } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Sprout, Calendar, Clock, CheckCircle, XCircle, AlertTriangle,
  ChevronLeft, ChevronRight, Loader2, User, LogOut, Shield, CalendarDays
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const DAY_LABELS = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };

function getMonday(dateStr) {
  const d = dateStr ? new Date(dateStr + 'T00:00:00') : new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff)).toISOString().split('T')[0];
}

function shiftWeek(ws, delta) {
  const d = new Date(ws + 'T00:00:00');
  d.setDate(d.getDate() + delta * 7);
  return d.toISOString().split('T')[0];
}

function formatWeek(ws) {
  const start = new Date(ws + 'T00:00:00');
  const end = new Date(start); end.setDate(start.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `${fmt(start)} – ${fmt(end)}`;
}

function formatDeadline(seconds) {
  if (seconds <= 0) return 'Passed';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function MySchedulePage() {
  const { user, logout } = useAuth();
  const [weekStart, setWeekStart] = useState(getMonday());
  const [data, setData] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [schedRes, compRes] = await Promise.all([getMySchedule(weekStart), getComplianceCheck()]);
      setData(schedRes.data);
      setCompliance(compRes.data);
    } catch (err) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleConfirmSingle = async (sessionId, action) => {
    setConfirming(sessionId);
    try {
      await confirmSessions({ schedule_id: data.schedule.id, confirmations: [{ session_id: sessionId, action }] });
      toast.success(action === 'confirm' ? 'Session confirmed' : 'Session declined');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setConfirming(null);
    }
  };

  const handleConfirmAll = async () => {
    setConfirming('all');
    try {
      await confirmAllSessions(data.schedule.id);
      toast.success('All sessions confirmed!');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm');
    } finally {
      setConfirming(null);
    }
  };

  const handleDeclineAll = async () => {
    setConfirming('decline-all');
    try {
      await declineAllSessions(data.schedule.id);
      toast.success('All sessions declined');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline');
    } finally {
      setConfirming(null);
    }
  };

  const schedule = data?.schedule;
  const pendingSessions = schedule?.sessions?.filter(s => s.status === 'pending') || [];
  const hasPending = pendingSessions.length > 0;

  // Group sessions by day
  const sessionsByDay = {};
  (schedule?.sessions || []).forEach(s => {
    if (!sessionsByDay[s.day]) sessionsByDay[s.day] = [];
    sessionsByDay[s.day].push(s);
  });

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#0A0A0A] rounded-xl flex items-center justify-center"><Sprout className="w-5 h-5 text-white" /></div>
              <h1 className="text-lg font-bold text-[#111827] tracking-tight">My Schedule</h1>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/dashboard"><Button variant="outline" size="sm" className="text-xs"><ChevronLeft className="w-3 h-3 mr-1" /> Dashboard</Button></Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="outline" size="sm" className="text-xs"><User className="w-3 h-3 mr-1" /> {user?.name}</Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild><Link to="/profile">Profile</Link></DropdownMenuItem>
                  <DropdownMenuItem onClick={logout}><LogOut className="w-3 h-3 mr-2" />Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Week Navigation */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <Button variant="outline" size="sm" onClick={() => setWeekStart(shiftWeek(weekStart, -1))} data-testid="prev-week"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="text-center px-4">
            <p className="text-sm font-semibold text-[#111827]" data-testid="week-display">{formatWeek(weekStart)}</p>
            <p className="text-[10px] text-[#9CA3AF]">Week of {weekStart}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setWeekStart(shiftWeek(weekStart, 1))} data-testid="next-week"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => setWeekStart(getMonday())} className="text-xs text-[#002FA7]">Today</Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" /></div>
        ) : !compliance?.fully_compliant ? (
          <Card className="border border-amber-200 bg-amber-50/30" data-testid="not-compliant">
            <CardContent className="p-6 text-center">
              <Shield className="w-12 h-12 text-amber-500 mx-auto mb-3" />
              <h2 className="text-lg font-bold text-[#111827] mb-2">Not Yet Eligible for Scheduling</h2>
              <p className="text-sm text-[#4B5563] mb-3">Complete these requirements first:</p>
              <div className="space-y-1">
                {(compliance?.issues || []).map((issue, i) => (
                  <div key={i} className="flex items-center gap-2 justify-center">
                    <XCircle className="w-3.5 h-3.5 text-red-500" /><span className="text-sm">{issue}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : !data?.has_schedule ? (
          <Card className="border border-dashed border-gray-300" data-testid="no-schedule">
            <CardContent className="p-10 text-center">
              <CalendarDays className="w-12 h-12 text-[#9CA3AF] mx-auto mb-3" />
              <h2 className="text-lg font-bold text-[#111827] mb-2">No Schedule This Week</h2>
              <p className="text-sm text-[#4B5563]">You have no sessions assigned for this week.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Deadline & Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              <Card className="border border-black/10" data-testid="total-hours-card">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-[#111827]">{schedule.total_hours}h</p>
                  <p className="text-xs text-[#4B5563]">Total Hours</p>
                </CardContent>
              </Card>
              <Card className="border border-black/10" data-testid="confirmed-hours-card">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-[#10B981]">{schedule.confirmed_hours}h</p>
                  <p className="text-xs text-[#4B5563]">Confirmed</p>
                </CardContent>
              </Card>
              <Card className={`border ${data.past_deadline ? 'border-red-200 bg-red-50/30' : 'border-blue-200 bg-blue-50/30'}`} data-testid="deadline-card">
                <CardContent className="p-4 text-center">
                  <p className={`text-2xl font-bold ${data.past_deadline ? 'text-red-600' : 'text-blue-600'}`}>
                    {data.past_deadline ? 'Passed' : formatDeadline(data.time_until_deadline)}
                  </p>
                  <p className="text-xs text-[#4B5563]">Deadline</p>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            {hasPending && !data.past_deadline && (
              <div className="flex gap-2 mb-6" data-testid="quick-actions">
                <Button onClick={handleConfirmAll} disabled={confirming === 'all'} className="bg-[#10B981] hover:bg-[#059669] flex-1" data-testid="confirm-all-btn">
                  {confirming === 'all' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                  Confirm All ({pendingSessions.length})
                </Button>
                <Button onClick={handleDeclineAll} disabled={confirming === 'decline-all'} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 flex-1" data-testid="decline-all-btn">
                  {confirming === 'decline-all' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
                  Decline All
                </Button>
              </div>
            )}

            {/* Sessions by Day */}
            <div className="space-y-3">
              {Object.entries(sessionsByDay).sort(([a], [b]) => {
                const days = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
                return days.indexOf(a) - days.indexOf(b);
              }).map(([day, sessions]) => (
                <Card key={day} className="border border-black/10" data-testid={`day-${day}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#002FA7]" />
                      {DAY_LABELS[day]}
                      <span className="text-xs font-normal text-[#9CA3AF]">{sessions[0].date}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-2">
                    {sessions.map((sess) => (
                      <div key={sess.session_id} className={`flex items-center justify-between p-3 rounded-lg border ${
                        sess.status === 'confirmed' ? 'bg-green-50/50 border-green-100' :
                        sess.status === 'declined' ? 'bg-red-50/50 border-red-100' :
                        sess.status === 'released' ? 'bg-amber-50/50 border-amber-100' :
                        'bg-white border-gray-100'
                      }`} data-testid={`session-${sess.session_id}`}>
                        <div className="flex items-center gap-3">
                          <div className="text-center">
                            <p className="text-sm font-bold text-[#111827]">{sess.start_time}</p>
                            <p className="text-[10px] text-[#9CA3AF]">{sess.end_time}</p>
                          </div>
                          <div className="border-l border-gray-200 pl-3">
                            <p className="text-sm font-medium text-[#111827]">{sess.subject || 'Session'}</p>
                            <div className="flex items-center gap-2">
                              {sess.student_name && <span className="text-xs text-[#4B5563]">{sess.student_name}</span>}
                              <span className="text-xs text-[#9CA3AF]">{sess.hours}h</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {sess.status === 'pending' && !data.past_deadline ? (
                            <>
                              <Button size="sm" onClick={() => handleConfirmSingle(sess.session_id, 'confirm')} disabled={confirming === sess.session_id} className="bg-[#10B981] hover:bg-[#059669] h-7 text-xs" data-testid={`confirm-${sess.session_id}`}>
                                {confirming === sess.session_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />} Confirm
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleConfirmSingle(sess.session_id, 'decline')} disabled={confirming === sess.session_id} className="border-red-200 text-red-600 h-7 text-xs" data-testid={`decline-${sess.session_id}`}>
                                <XCircle className="w-3 h-3 mr-1" /> Decline
                              </Button>
                            </>
                          ) : (
                            <Badge className={
                              sess.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                              sess.status === 'declined' ? 'bg-red-100 text-red-700' :
                              sess.status === 'released' ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-100 text-gray-600'
                            }>{sess.status}</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
